"use client";

import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { auth } from "@/app/lib/firebase";
import { useRouter } from "next/navigation";
import { useState, useEffect, useLayoutEffect, useRef } from "react";
import Link from "next/link";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "@/app/lib/firebase";
import { mixpanelTrack } from "@/app/lib/mixpanel";

function isMobile(): boolean {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent.toLowerCase();
  const mobileKeywords = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/;
  return mobileKeywords.test(ua) || window.innerWidth < 768;
}

async function createGoogleProfileIfNeeded(user: { uid: string; displayName: string | null; email: string | null }) {
  const profileRef = doc(db, "profiles", user.uid);
  const profileSnap = await getDoc(profileRef);
  if (profileSnap.exists()) return;
  const fullName = user.displayName?.trim() || "";
  const parts = fullName.split(/\s+/).filter(Boolean);
  const name = parts[0] || "";
  const surname = parts.slice(1).join(" ") || "";
  const displayName = fullName || user.email?.split("@")[0] || "Korisnik";
  const rawUsername =
    fullName.replace(/\s+/g, "_").toLowerCase().replace(/[^a-z0-9_]/g, "") ||
    user.email?.split("@")[0]?.replace(/[^a-z0-9_]/gi, "").toLowerCase() ||
    "korisnik";
  const username =
    rawUsername.length >= 3 ? rawUsername : rawUsername + "1".repeat(Math.max(0, 3 - rawUsername.length));
  await setDoc(
    profileRef,
    {
      id: user.uid,
      name: name || undefined,
      surname: surname || undefined,
      username: username || undefined,
      displayName,
      email: user.email || undefined,
      memberSince: Timestamp.fromMillis(Date.now()),
    },
    { merge: true }
  );
}

export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [checkingRedirect, setCheckingRedirect] = useState(true);
  const redirectHandled = useRef(false);

  // Nakon povratka sa Google redirect-a (mobil) dovrši prijavu i kreiraj profil ako treba.
  // useLayoutEffect da se pokrene što prije; jedan poziv (ref) da ne pojede rezultat dva puta.
  useLayoutEffect(() => {
    if (redirectHandled.current) {
      setCheckingRedirect(false);
      return;
    }
    const fallback = setTimeout(() => {
      if (!redirectHandled.current) {
        redirectHandled.current = true;
        setCheckingRedirect(false);
      }
    }, 2500);
    getRedirectResult(auth)
      .then(async (result) => {
        redirectHandled.current = true;
        setCheckingRedirect(false);
        if (!result?.user) return;
        try {
          await createGoogleProfileIfNeeded(result.user);
        } catch (_) {
          // Korisnik je ulogovan i bez profila; prebaci ga, profil može kasnije
        }
        mixpanelTrack("Sign In", {
          user_id: result.user.uid,
          login_method: "google",
          success: true,
        });
        router.replace("/");
      })
      .catch((e: any) => {
        redirectHandled.current = true;
        setCheckingRedirect(false);
        const code = e?.code;
        if (code === "auth/account-exists-with-different-credential") {
          setErr(
            "Ovaj email je već povezan s drugim načinom prijave. Pokušajte se prijaviti emailom i lozinkom."
          );
        } else if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
          setErr(e?.message ?? "Greška pri prijavi sa Google-om. Pokušajte ponovo.");
        }
      })
      .finally(() => clearTimeout(fallback));
  }, [router]);

  const signInWithGoogle = async () => {
    setErr(null);
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      if (isMobile()) {
        await signInWithRedirect(auth, provider);
        return;
      }
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      await createGoogleProfileIfNeeded(user);
      mixpanelTrack("Sign In", {
        user_id: user.uid,
        login_method: "google",
        success: true,
      });
      router.replace("/");
    } catch (e: any) {
      const code = e?.code;
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        return;
      }
      if (code === "auth/account-exists-with-different-credential") {
        setErr(
          "Ovaj email je već povezan s drugim načinom prijave. Pokušajte se prijaviti emailom i lozinkom."
        );
        return;
      }
      if (code === "auth/popup-blocked") {
        setErr("Popup je blokiran. Dozvolite popup prozore za ovu stranicu.");
        return;
      }
      setErr(e?.message ?? "Greška pri prijavi sa Google-om. Pokušajte ponovo.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), pass);
      mixpanelTrack("Sign In", {
        user_id: userCredential.user.uid,
        login_method: "email",
        success: true,
      });
      router.replace("/");
    } catch (e: any) {
      const errorCode = e?.code;
      let errorMessage = "Greška pri prijavi.";
      
      // Provjeri da li je nalog obrisan
      if (errorCode === "auth/invalid-credential" || errorCode === "auth/user-not-found" || errorCode === "auth/wrong-password") {
        try {
          // Pokušaj pronaći korisnika po email-u (ako imamo uid iz greške ili pokušajmo pronaći)
          // Ako je nalog obrisan, možda još postoji u deletedUsers
          // Najbolje je provjeriti nakon što se korisnik uloguje, ali ovdje možemo samo prikazati lijepu poruku
        } catch {}
      }
      
      // Lijepo formatirane poruke za različite greške
      switch (errorCode) {
        case "auth/invalid-credential":
          errorMessage = "Email adresa ili lozinka nisu ispravni. Provjerite svoje podatke i pokušajte ponovo.";
          break;
        case "auth/user-not-found":
          errorMessage = "Nalog sa ovom email adresom ne postoji. Provjerite email adresu ili registrujte novi nalog.";
          break;
        case "auth/wrong-password":
          errorMessage = "Pogrešna lozinka. Provjerite svoju lozinku i pokušajte ponovo.";
          break;
        case "auth/invalid-email":
          errorMessage = "Neispravna email adresa. Molimo unesite validnu email adresu.";
          break;
        case "auth/user-disabled":
          errorMessage = "Ovaj nalog je onemogućen. Kontaktirajte administratora za pomoć.";
          break;
        case "auth/too-many-requests":
          errorMessage = "Previše neuspješnih pokušaja prijave. Molimo sačekajte nekoliko minuta prije ponovnog pokušaja.";
          break;
        case "auth/network-request-failed":
          errorMessage = "Problem sa internet konekcijom. Provjerite svoju konekciju i pokušajte ponovo.";
          break;
        default:
          // Ako je generička greška, pokušaj izvući korisniju poruku
          if (e?.message) {
            // Ako poruka sadrži "invalid-credential" ili slično, koristi našu poruku
            if (e.message.includes("invalid-credential") || e.message.includes("invalid credential")) {
              errorMessage = "Email adresa ili lozinka nisu ispravni. Provjerite svoje podatke i pokušajte ponovo.";
            } else if (e.message.includes("user-not-found")) {
              errorMessage = "Nalog sa ovom email adresom ne postoji. Provjerite email adresu ili registrujte novi nalog.";
            } else {
              errorMessage = e.message;
            }
          }
      }
      
      setErr(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    
    if (!resetEmail.trim()) {
      setErr("Unesite email adresu.");
      return;
    }
    
    setResetLoading(true);
    try {
      const actionCodeSettings = {
        url: typeof window !== "undefined" 
          ? `${window.location.origin}/reset-password` 
          : "http://localhost:3000/reset-password",
        handleCodeInApp: true,
      };
      
      await sendPasswordResetEmail(auth, resetEmail.trim(), actionCodeSettings);
      setResetSuccess(true);
      setResetEmail("");
    } catch (e: any) {
      console.error("Password reset error:", e);
      if (e?.code === "auth/user-not-found") {
        setErr("Email adresa nije pronađena. Provjerite da li ste unijeli ispravnu email adresu.");
      } else if (e?.code === "auth/invalid-email") {
        setErr("Neispravna email adresa. Molimo unesite validnu email adresu.");
      } else if (e?.code === "auth/too-many-requests") {
        setErr("Previše zahtjeva. Molimo pokušajte kasnije.");
      } else {
        setErr(e?.message ?? "Greška pri slanju email-a za reset lozinke. Provjerite Firebase konfiguraciju.");
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-3 sm:px-4 md:px-6 py-6 sm:py-8 md:py-12 bg-[var(--bg)]">
      <div className="w-full max-w-md">
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-sm">
          <div className="text-center mb-4 sm:mb-6 md:mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[var(--accent)] mb-1 sm:mb-2">
              Prijava
            </h1>
            <p className="text-[var(--muted)] text-xs sm:text-sm">
              Dobrodošli na NOSIVA
            </p>
          </div>

          {checkingRedirect ? (
            <div className="py-8 text-center text-[var(--muted)]">
              <p className="font-medium">Dovršavanje prijave…</p>
              <p className="text-sm mt-1">Sačekajte trenutak.</p>
            </div>
          ) : showForgotPassword ? (
            <div className="space-y-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[var(--text)]">Reset lozinke</h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setResetEmail("");
                    setErr(null);
                    setResetSuccess(false);
                  }}
                  className="text-[var(--muted)] hover:text-[var(--text)]"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 6L6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {resetSuccess ? (
                <div className="bg-[var(--accent)]/10 border border-[var(--accent)]/30 text-[var(--accent)] px-4 py-3 rounded-xl text-sm space-y-2">
                  <p className="font-semibold">✓ Email za reset lozinke je poslan!</p>
                  <p>Provjerite svoj inbox. Ako email ne stigne u nekoliko minuta, provjerite i spam folder.</p>
                  <p className="text-xs opacity-80">Email dolazi sa adrese noreply@firebaseapp.com</p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-[var(--muted)]">
                    Unesite svoju email adresu i poslat ćemo vam link za reset lozinke.
                  </p>
                  <form onSubmit={handleForgotPassword} className="space-y-3">
                    <input
                      className="w-full px-4 py-3 border border-[var(--border)] bg-[var(--card)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-[var(--text)] placeholder:text-[var(--muted)]"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      type="email"
                      placeholder="ime@primjer.com"
                      required
                    />
                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="w-full py-3 rounded-xl bg-[var(--accent)] text-white font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {resetLoading ? "Slanje..." : "Pošalji reset link"}
                    </button>
                  </form>
                </>
              )}
              {err && (
                <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-600 dark:text-red-400">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-red-600 dark:text-red-400 text-sm mb-1">
                        Greška
                      </p>
                      <p className="text-red-600/90 dark:text-red-400/90 text-sm leading-relaxed">
                        {err}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold mb-2 text-[var(--text)]">
                  Email adresa
                </label>
                <input
                  className="w-full px-4 py-3 border border-[var(--border)] bg-[var(--card)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-[var(--text)] placeholder:text-[var(--muted)]"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  placeholder="ime@primjer.com"
                  required
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-semibold text-[var(--text)]">
                    Lozinka
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setShowForgotPassword(true);
                      setErr(null);
                    }}
                    className="text-sm text-[var(--accent)] hover:underline font-medium"
                  >
                    Zaboravljena lozinka?
                  </button>
                </div>
                <div className="relative">
                  <input
                    className="w-full px-4 py-3 pr-14 border border-[var(--border)] bg-[var(--card)] rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/20 focus:border-[var(--accent)] transition text-[var(--text)] placeholder:text-[var(--muted)]"
                    value={pass}
                    onChange={(e) => setPass(e.target.value)}
                    type={showPassword ? "text" : "password"}
                    placeholder="Unesite lozinku"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-[var(--muted)] hover:text-[var(--text)] transition"
                    aria-label={showPassword ? "Sakrij lozinku" : "Prikaži lozinku"}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {err && (
                <div className="bg-red-500/10 border-2 border-red-500/30 rounded-xl p-4 space-y-2">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-red-600 dark:text-red-400">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-red-600 dark:text-red-400 text-sm mb-1">
                        Greška pri prijavi
                      </p>
                      <p className="text-red-600/90 dark:text-red-400/90 text-sm leading-relaxed">
                        {err}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl bg-[var(--accent)] text-white font-bold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed transition shadow-sm hover:shadow-md"
              >
                {loading ? "Prijava u toku…" : "Prijavi se"}
              </button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[var(--border)]" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-[var(--card)] text-[var(--muted)]">
                    ili
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={signInWithGoogle}
                disabled={loading || googleLoading}
                className="w-full py-3.5 rounded-xl border-2 border-[var(--border)] bg-[var(--card)] text-[var(--text)] font-semibold hover:bg-[var(--border)]/30 disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-3"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                {googleLoading ? "Prijava…" : "Prijavi se sa Google-om"}
              </button>

              <p className="text-sm text-center text-[var(--muted)] mt-6">
                Nemaš nalog?{" "}
                <Link
                  className="font-semibold text-[var(--accent)] hover:underline"
                  href="/register"
                >
                  Registruj se
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
